import { formatMoney } from "@/lib/market";
import { Link } from "react-router-dom";
import { applyMarket, applyMarketOne, isBR, MARKET } from "@/lib/market";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, Percent, Tag } from "lucide-react";
import { cn } from "@/lib/utils";

interface PromotionProduct {
  id: string;
  name: string;
  slug: string;
  short_description: string | null;
  price: number | null;
  image_url: string | null;
  promotion_label: string | null;
  type: "equipment" | "consumable";
}

export function PromotionsSection() {
  // Fetch promoted equipment
  const { data: promotedEquipment } = useQuery({
    queryKey: ["promoted-equipment"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("equipment")
        .select("id, name, slug, short_description, price, image_url, promotion_label")
        .eq("is_active", true)
        .eq("is_promotion", true)
        .limit(5);
      if (error) throw error;
      return data?.map(item => ({ ...item, type: "equipment" as const })) || [];
    },
  });

  // Fetch promoted consumables with first variant price
  const { data: promotedConsumables } = useQuery({
    queryKey: ["promoted-consumables", MARKET],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("consumables")
        .select(`
          id, name, slug, short_description, price, price_brl, visible_br, image_url, promotion_label, has_variants,
          consumable_variants (price, display_order)
        `)
        .eq("is_active", true)
        .eq("is_promotion", true)
        .limit(5);
      if (error) throw error;
      
      return applyMarket(data as any[])?.map((item: any) => {
        // If has variants, use first variant price
        let effectivePrice = item.price;
        if (item.has_variants && item.consumable_variants?.length > 0) {
          const sortedVariants = [...item.consumable_variants].sort(
            (a, b) => (a.display_order || 0) - (b.display_order || 0)
          );
          effectivePrice = sortedVariants[0]?.price ?? item.price;
        }
        return { 
          ...item, 
          price: effectivePrice,
          type: "consumable" as const 
        };
      }) || [];
    },
  });

  // Combine and limit to 10 products
  const promotedProducts: PromotionProduct[] = [
    ...(promotedEquipment || []),
    ...(promotedConsumables || []),
  ].slice(0, 10);

  // Don't render if no promotions
  if (!promotedProducts || promotedProducts.length === 0) {
    return null;
  }

  return (
    <section className="py-10 md:py-14 bg-gradient-to-br from-destructive/5 via-background to-primary/5">
      <div className="container">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Percent className="h-5 w-5 text-destructive-text" />
              <p className="text-destructive-text font-semibold uppercase tracking-wider text-sm">
                Promoções
              </p>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">
              Ofertas Especiais
            </h2>
            <p className="text-muted-foreground mt-2">
              Aproveite os nossos produtos em promoção com preços visíveis para todos
            </p>
          </div>
          <Button variant="outline" asChild className="border-destructive-text text-destructive-text hover:bg-destructive hover:text-destructive-foreground w-fit">
            <Link to="/shop?promocao=true">
              Ver Todas as Promoções
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
          {promotedProducts.map((product) => {
            const productUrl = product.type === "equipment" 
              ? `/equipamento/${product.slug}` 
              : `/consumivel/${product.slug}`;
            
            return (
              <Link key={`${product.type}-${product.id}`} to={productUrl}>
                <Card className="h-full overflow-hidden card-hover group relative">
                  {/* Promotion Badge */}
                  <div className="absolute top-2 left-2 z-10">
                    <Badge 
                      variant="destructive" 
                      className="shadow-md text-xs gap-1 px-2 py-1"
                    >
                      <Tag className="h-3 w-3" />
                      {product.promotion_label || "Promoção"}
                    </Badge>
                  </div>
                  
                  <div className="aspect-[4/3] overflow-hidden bg-muted">
                    {product.image_url ? (
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                        Sem imagem
                      </div>
                    )}
                  </div>
                  <CardContent className="p-3">
                    <h3 className="font-semibold mb-1 line-clamp-1 group-hover:text-primary transition-colors">
                      {product.name}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                      {product.short_description}
                    </p>
                    {product.price && (
                      <span className="text-lg font-bold text-destructive-text">
                        {formatMoney(product.price)}
                      </span>
                    )}
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
